import React, { useState, useRef, useEffect, useCallback } from 'react';
import { useAuth } from '../../contexts/AuthContext';

const toolBtn = {
  padding: '4px 8px', background: '#2A3441', color: '#C9D1D9',
  border: 'none', borderRadius: 5, fontSize: 11, cursor: 'pointer',
};

export default function ShortformEditor() {
  const { api } = useAuth();
  const [videoFile, setVideoFile] = useState(null);
  const [videoUrl, setVideoUrl] = useState(null);
  const [guideText, setGuideText] = useState('');
  const [phase, setPhase] = useState('input'); // input | analyzing | review | edit
  const [progress, setProgress] = useState({ percent: 0, message: '' });
  const [analysis, setAnalysis] = useState(null); // { shortforms, transcript }
  const [generating, setGenerating] = useState({});
  const [done, setDone] = useState({});
  const videoInputRef = useRef(null);

  // edit 상태
  const [editingIdx, setEditingIdx] = useState(null);
  const [editClips, setEditClips] = useState({});
  const [clipboard, setClipboard] = useState(null);
  const [selectedClip, setSelectedClip] = useState(null);
  const canvasRef = useRef(null);
  const editVideoRef = useRef(null);
  const [editTime, setEditTime] = useState(0);
  const [editDuration, setEditDuration] = useState(0);
  const [tlPixelsPerSec, setTlPixelsPerSec] = useState(20);

  const [previewUrl, setPreviewUrl] = useState(null);
  const [previewing, setPreviewing] = useState(false);

  const shortforms = analysis?.shortforms || [];
  const clips = editClips[editingIdx] || [];

  const handleVideoSelect = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setVideoFile(file);
    setVideoUrl(URL.createObjectURL(file));
    setAnalysis(null);
    setDone({});
    setEditClips({});
    setPhase('input');
  };

  // ── 분석 (SSE: STT + Claude) ──
  const analyze = async () => {
    if (!videoFile) { alert('영상 파일을 선택해주세요'); return; }
    setPhase('analyzing');
    setProgress({ percent: 0, message: '업로드 중...' });
    setAnalysis(null);

    try {
      const formData = new FormData();
      formData.append('video', videoFile);
      if (guideText.trim()) formData.append('guideText', guideText);

      const token = localStorage.getItem('accessToken');
      const res = await fetch('/api/v3/shortform/analyze', {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
        body: formData,
      });

      if (!res.ok) {
        const errText = await res.text();
        throw new Error(`서버 오류 ${res.status}: ${errText.slice(0, 200)}`);
      }

      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';

      while (true) {
        const { done: d, value } = await reader.read();
        if (d) break;
        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split('\n');
        buffer = lines.pop() || '';
        for (const line of lines) {
          if (!line.startsWith('data: ')) continue;
          try {
            const data = JSON.parse(line.slice(6));
            switch (data.type) {
              case 'phase':
                setProgress(p => ({ ...p, message: data.message }));
                break;
              case 'progress':
                setProgress({ percent: data.percent || 0, message: data.message || '' });
                break;
              case 'stt_done':
                setProgress({ percent: 70, message: `STT 완료 (${data.segmentCount}개 구간). AI 분석 중...` });
                break;
              case 'done':
                setAnalysis({ shortforms: data.shortforms, transcript: data.transcript });
                setPhase('review');
                break;
              case 'error':
                alert('분석 오류: ' + data.message);
                setPhase('input');
                break;
              default:
                break;
            }
          } catch {}
        }
      }
    } catch (e) {
      alert('분석 실패: ' + e.message);
      setPhase('input');
    }
  };

  // ── 숏폼 영상 생성 (SSE) ──
  const generate = async (sf) => {
    if (!videoFile) { alert('영상 파일이 필요합니다'); return; }
    setGenerating(prev => ({ ...prev, [sf.index]: true }));
    try {
      const formData = new FormData();
      formData.append('video', videoFile);
      formData.append('shortform', JSON.stringify(sf));

      const token = localStorage.getItem('accessToken');
      const res = await fetch('/api/v3/shortform/generate', {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
        body: formData,
      });
      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';
      while (true) {
        const { done: d, value } = await reader.read();
        if (d) break;
        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split('\n');
        buffer = lines.pop() || '';
        for (const line of lines) {
          if (!line.startsWith('data: ')) continue;
          try {
            const data = JSON.parse(line.slice(6));
            if (data.type === 'done') setDone(prev => ({ ...prev, [sf.index]: data }));
          } catch {}
        }
      }
    } catch (e) {
      alert('생성 실패: ' + e.message);
    }
    setGenerating(prev => ({ ...prev, [sf.index]: false }));
  };

  // ── Canvas 타임라인 ──
  const drawCanvas = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    const h = 120;
    const pps = tlPixelsPerSec;

    ctx.fillStyle = '#0D1117';
    ctx.fillRect(0, 0, canvas.width, h);

    let timelinePos = 0;
    clips.forEach((clip, i) => {
      const dur = clip.end - clip.start;
      const x = timelinePos * pps;
      const w = Math.max(dur * pps - 2, 4);
      const isSelected = selectedClip?.sfIndex === editingIdx && selectedClip?.clipIdx === i;

      ctx.fillStyle = isSelected ? '#1565C0' : '#2E7D32';
      ctx.fillRect(x, 30, w, 60);

      if (isSelected) {
        ctx.strokeStyle = '#64B5F6';
        ctx.lineWidth = 2;
        ctx.strokeRect(x + 1, 31, w - 2, 58);
      }

      ctx.fillStyle = '#fff';
      ctx.font = 'bold 11px sans-serif';
      ctx.fillText(`${i + 1}`, x + 4, 46);

      if (w > 60) {
        ctx.font = '9px sans-serif';
        ctx.fillStyle = 'rgba(255,255,255,0.8)';
        const maxLen = Math.floor(w / 6);
        ctx.fillText((clip.text || '').slice(0, maxLen), x + 4, 78);
      }

      ctx.fillStyle = 'rgba(255,255,255,0.5)';
      ctx.font = '8px monospace';
      ctx.fillText(`${clip.start}~${clip.end}s`, x + 4, 88);

      timelinePos += dur;
    });

    const cursorX = editTime * pps;
    ctx.strokeStyle = '#fff';
    ctx.lineWidth = 2;
    ctx.beginPath();
    ctx.moveTo(cursorX, 0);
    ctx.lineTo(cursorX, h);
    ctx.stroke();
  }, [clips, editTime, selectedClip, tlPixelsPerSec, editingIdx]);

  useEffect(() => { drawCanvas(); }, [drawCanvas]);

  const handleCanvasClick = (e) => {
    const rect = canvasRef.current.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const pps = tlPixelsPerSec;
    let timelinePos = 0;
    for (let i = 0; i < clips.length; i++) {
      const dur = clips[i].end - clips[i].start;
      const clipX = timelinePos * pps;
      const clipW = dur * pps;
      if (x >= clipX && x < clipX + clipW) {
        setSelectedClip({ sfIndex: editingIdx, clipIdx: i });
        if (editVideoRef.current) editVideoRef.current.currentTime = clips[i].start;
        return;
      }
      timelinePos += dur;
    }
    setSelectedClip(null);
  };

  // ── 편집 도구 ──
  const moveClipUp = useCallback(() => {
    if (!selectedClip || selectedClip.clipIdx === 0) return;
    const i = selectedClip.clipIdx;
    setEditClips(prev => {
      const arr = [...(prev[editingIdx] || [])];
      [arr[i - 1], arr[i]] = [arr[i], arr[i - 1]];
      return { ...prev, [editingIdx]: arr };
    });
    setSelectedClip(prev => ({ ...prev, clipIdx: i - 1 }));
  }, [selectedClip, editingIdx]);

  const moveClipDown = useCallback(() => {
    if (!selectedClip) return;
    const i = selectedClip.clipIdx;
    if (i >= clips.length - 1) return;
    setEditClips(prev => {
      const arr = [...(prev[editingIdx] || [])];
      [arr[i], arr[i + 1]] = [arr[i + 1], arr[i]];
      return { ...prev, [editingIdx]: arr };
    });
    setSelectedClip(prev => ({ ...prev, clipIdx: i + 1 }));
  }, [selectedClip, clips.length, editingIdx]);

  const copyClip = useCallback(() => {
    if (!selectedClip) return;
    const clip = clips[selectedClip.clipIdx];
    setClipboard({ ...clip, id: `copied_${Date.now()}` });
  }, [selectedClip, clips]);

  const pasteClip = useCallback(() => {
    if (!clipboard) return;
    const insertIdx = selectedClip ? selectedClip.clipIdx + 1 : clips.length;
    setEditClips(prev => {
      const arr = [...(prev[editingIdx] || [])];
      arr.splice(insertIdx, 0, { ...clipboard, id: `paste_${Date.now()}` });
      return { ...prev, [editingIdx]: arr };
    });
  }, [clipboard, selectedClip, clips.length, editingIdx]);

  const deleteClip = useCallback(() => {
    if (!selectedClip) return;
    const i = selectedClip.clipIdx;
    setEditClips(prev => {
      const arr = [...(prev[editingIdx] || [])];
      arr.splice(i, 1);
      return { ...prev, [editingIdx]: arr };
    });
    setSelectedClip(null);
  }, [selectedClip, editingIdx]);

  const resetClips = () => {
    const sf = analysis?.shortforms?.find(s => s.index === editingIdx);
    if (!sf) return;
    setEditClips(prev => ({
      ...prev,
      [editingIdx]: sf.segments.map((seg, i) => ({ ...seg, id: `${editingIdx}_${i}_reset` })),
    }));
    setSelectedClip(null);
  };

  // 키보드 단축키
  useEffect(() => {
    const handler = (e) => {
      if (phase !== 'edit') return;
      if (e.ctrlKey || e.metaKey) {
        if (e.key === 'c') { e.preventDefault(); copyClip(); }
        if (e.key === 'v') { e.preventDefault(); pasteClip(); }
      }
      if (e.key === 'Delete' || e.key === 'Backspace') deleteClip();
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [phase, copyClip, pasteClip, deleteClip]);

  // ── 편집 화면 진입 (미리보기 생성 포함) ──
  const handleEdit = async (sf) => {
    if (!editClips[sf.index]) {
      setEditClips(prev => ({
        ...prev,
        [sf.index]: sf.segments.map((seg, i) => ({
          ...seg,
          id: `${sf.index}_${i}_${Date.now()}`,
        })),
      }));
    }
    setEditingIdx(sf.index);
    setPreviewUrl(null);
    setPreviewing(true);
    setPhase('edit');

    // 미리보기 영상 생성
    try {
      const formData = new FormData();
      formData.append('video', videoFile);
      formData.append('shortform', JSON.stringify(sf));
      const token = localStorage.getItem('accessToken');
      const res = await fetch('/api/v3/shortform/preview', {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
        body: formData,
      });
      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';
      while (true) {
        const { done: d, value } = await reader.read();
        if (d) break;
        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split('\n');
        buffer = lines.pop() || '';
        for (const line of lines) {
          if (!line.startsWith('data: ')) continue;
          try {
            const data = JSON.parse(line.slice(6));
            if (data.type === 'done') setPreviewUrl(data.previewUrl);
            if (data.type === 'error') alert('미리보기 생성 실패: ' + data.message);
          } catch {}
        }
      }
    } catch (e) {
      alert('미리보기 실패: ' + e.message);
    }
    setPreviewing(false);
  };

  // ── 렌더링 ──
  return (
    <div style={{ display: 'flex', height: '100vh', background: '#0D1117', color: '#E6EDF3', overflow: 'hidden' }}>

      {/* ── 좌측 설정 패널 (input/review만) ── */}
      {phase !== 'edit' && (
        <div style={{ width: 360, flexShrink: 0, display: 'flex', flexDirection: 'column', borderRight: '1px solid #2A3441', overflow: 'hidden' }}>
          <div style={{ padding: '16px 20px', borderBottom: '1px solid #2A3441' }}>
            <h2 style={{ margin: 0, fontSize: 18, fontWeight: 700 }}>🎬 숏폼 자동편집기</h2>
          </div>

          <div style={{ flex: 1, overflowY: 'auto', padding: 20, display: 'flex', flexDirection: 'column', gap: 20 }}>

            {/* 영상 업로드 */}
            <div>
              <label style={{ fontSize: 13, fontWeight: 600, color: '#8B949E', display: 'block', marginBottom: 8 }}>📹 영상 파일</label>
              <div
                onClick={() => videoInputRef.current?.click()}
                onDragOver={e => { e.preventDefault(); e.stopPropagation(); }}
                onDragEnter={e => { e.preventDefault(); e.stopPropagation(); }}
                onDrop={e => {
                  e.preventDefault();
                  e.stopPropagation();
                  const file = e.dataTransfer.files[0];
                  if (file && file.type.startsWith('video/')) {
                    handleVideoSelect({ target: { files: [file] } });
                  }
                }}
                style={{ padding: 16, border: '2px dashed #2A3441', borderRadius: 10, textAlign: 'center', cursor: 'pointer', background: videoFile ? 'rgba(76,175,80,0.05)' : 'transparent', borderColor: videoFile ? '#4CAF50' : '#2A3441' }}>
                {videoFile ? (
                  <span style={{ color: '#4CAF50', fontSize: 13 }}>✅ {videoFile.name}</span>
                ) : (
                  <span style={{ color: '#6B7280', fontSize: 13 }}>클릭 또는 드래그하여 영상 선택</span>
                )}
              </div>
              <input ref={videoInputRef} type="file" accept="video/*" style={{ display: 'none' }} onChange={handleVideoSelect} />
            </div>

            {/* 가이드 */}
            <div>
              <label style={{ fontSize: 13, fontWeight: 600, color: '#8B949E', display: 'block', marginBottom: 8 }}>📋 숏폼 가이드 (선택)</label>
              <textarea
                value={guideText}
                onChange={e => setGuideText(e.target.value)}
                placeholder="숏폼 가이드를 입력하세요&#10;예) 성조숙증 관련 핵심 메시지 위주로, 부모 타겟, 후킹은 충격적 사실로 시작"
                rows={5}
                style={{ width: '100%', padding: 12, background: '#161B22', border: '1px solid #2A3441', borderRadius: 8, color: '#E6EDF3', fontSize: 12, fontFamily: 'inherit', resize: 'vertical', boxSizing: 'border-box' }}
              />
            </div>

            {/* 분석 버튼 */}
            <button onClick={analyze} disabled={phase === 'analyzing'}
              style={{ padding: '14px', background: phase === 'analyzing' ? '#2A3441' : '#2E7D32', color: '#fff', border: 'none', borderRadius: 10, fontSize: 15, fontWeight: 700, cursor: phase === 'analyzing' ? 'not-allowed' : 'pointer' }}>
              {phase === 'analyzing' ? '🤖 분석 중...' : '🤖 STT + AI 숏폼 분석'}
            </button>

            {shortforms.length > 0 && (
              <button onClick={() => shortforms.forEach(sf => generate(sf))}
                style={{ padding: '12px', background: '#1565C0', color: '#fff', border: 'none', borderRadius: 10, fontSize: 14, fontWeight: 700, cursor: 'pointer' }}>
                ⚡ 전체 생성 ({shortforms.length}개)
              </button>
            )}
          </div>
        </div>
      )}

      {/* ── 메인 패널 ── */}
      <div style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>

        {phase === 'edit' && editingIdx !== null ? (
          /* ════ EDIT 화면 ════ */
          <div style={{ display: 'flex', flexDirection: 'column', height: '100%', overflow: 'hidden' }}>

            {/* 상단 탭 */}
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '10px 16px', borderBottom: '1px solid #2A3441', flexShrink: 0 }}>
              <button onClick={() => { setPhase('review'); setEditingIdx(null); }}
                style={{ padding: '5px 12px', background: '#2A3441', color: '#8B949E', border: 'none', borderRadius: 6, fontSize: 12, cursor: 'pointer' }}>
                ← 목록
              </button>
              <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                {shortforms.map(sf => (
                  <button key={sf.index}
                    onClick={() => handleEdit(sf)}
                    style={{ padding: '5px 14px', background: editingIdx === sf.index ? '#2E7D32' : '#161B22', color: editingIdx === sf.index ? '#fff' : '#8B949E', border: '1px solid #2A3441', borderRadius: 6, fontSize: 12, cursor: 'pointer' }}>
                    숏폼 #{sf.index}
                  </button>
                ))}
              </div>
              <div style={{ flex: 1 }} />
              <button onClick={() => {
                const sf = analysis.shortforms.find(s => s.index === editingIdx);
                if (sf) {
                  const editedSF = { ...sf, segments: clips.map((c, i) => ({ ...c, order: i + 1 })) };
                  generate(editedSF);
                  setPhase('review');
                }
              }}
                style={{ padding: '6px 16px', background: '#1565C0', color: '#fff', border: 'none', borderRadius: 8, fontSize: 13, fontWeight: 700, cursor: 'pointer' }}>
                ⚡ 이 숏폼 생성
              </button>
            </div>

            {/* 메인 */}
            <div style={{ flex: 1, display: 'flex', overflow: 'hidden' }}>

              {/* 좌측: 영상 + Canvas */}
              <div style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>

                <div style={{ flex: 1, background: '#000', position: 'relative', minHeight: 0 }}>
                  <video ref={editVideoRef}
                    src={previewUrl || videoUrl || ''}
                    style={{ width: '100%', height: '100%', objectFit: 'contain' }}
                    onTimeUpdate={e => setEditTime(e.target.currentTime)}
                    onLoadedMetadata={e => setEditDuration(e.target.duration)}
                    controls />
                  {previewing && (
                    <div style={{ position: 'absolute', inset: 0, background: 'rgba(0,0,0,0.8)', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', zIndex: 10 }}>
                      <div style={{ width: 40, height: 40, border: '4px solid #2A3441', borderTop: '4px solid #4CAF50', borderRadius: '50%', animation: 'spin 1s linear infinite', marginBottom: 12 }} />
                      <p style={{ color: '#8B949E', fontSize: 14 }}>숏폼 미리보기 생성 중...</p>
                    </div>
                  )}
                </div>

                <div style={{ height: 120, flexShrink: 0, background: '#0D1117', borderTop: '1px solid #2A3441', overflowX: 'auto' }}>
                  <canvas ref={canvasRef}
                    width={Math.max(clips.reduce((s, c) => s + (c.end - c.start), 0) * tlPixelsPerSec + 100, 800)}
                    height={120}
                    style={{ display: 'block', height: '120px', cursor: 'pointer' }}
                    onClick={handleCanvasClick} />
                </div>

                <div style={{ height: 44, flexShrink: 0, display: 'flex', alignItems: 'center', gap: 6, padding: '0 12px', background: '#161B22', borderTop: '1px solid #2A3441' }}>
                  <button onClick={() => setTlPixelsPerSec(p => Math.max(p - 5, 5))} style={toolBtn}>🔍-</button>
                  <span style={{ fontSize: 10, color: '#6B7280', minWidth: 30, textAlign: 'center' }}>{tlPixelsPerSec}px</span>
                  <button onClick={() => setTlPixelsPerSec(p => Math.min(p + 5, 100))} style={toolBtn}>🔍+</button>
                  <div style={{ width: 1, height: 20, background: '#2A3441', margin: '0 4px' }} />
                  <button onClick={moveClipUp} disabled={!selectedClip || selectedClip.clipIdx === 0} style={toolBtn}>⬅ 앞으로</button>
                  <button onClick={moveClipDown} disabled={!selectedClip || selectedClip.clipIdx >= clips.length - 1} style={toolBtn}>뒤로 ➡</button>
                  <div style={{ width: 1, height: 20, background: '#2A3441', margin: '0 4px' }} />
                  <button onClick={copyClip} disabled={!selectedClip} style={toolBtn}>📋 복사</button>
                  <button onClick={pasteClip} disabled={!clipboard} style={toolBtn}>📌 붙여넣기</button>
                  <div style={{ width: 1, height: 20, background: '#2A3441', margin: '0 4px' }} />
                  <button onClick={deleteClip} disabled={!selectedClip} style={{ ...toolBtn, color: '#F44336' }}>🗑️ 삭제</button>
                  <button onClick={resetClips} style={{ ...toolBtn, color: '#FF9800' }}>↩️ 초기화</button>
                  <div style={{ flex: 1 }} />
                  <span style={{ fontSize: 11, color: '#6B7280' }}>Ctrl+C 복사 | Ctrl+V 붙여넣기 | Del 삭제</span>
                </div>
              </div>

              {/* 우측: 구간 목록 */}
              <div style={{ width: 280, flexShrink: 0, borderLeft: '1px solid #2A3441', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
                <div style={{ padding: '10px 14px', borderBottom: '1px solid #2A3441', fontSize: 12, color: '#8B949E', fontWeight: 600 }}>
                  편집 구간 ({clips.length}개)
                  {clipboard && <span style={{ marginLeft: 8, color: '#FF9800', fontSize: 11 }}>📋 클립보드: {clipboard.text?.slice(0, 10)}...</span>}
                </div>
                <div style={{ flex: 1, overflowY: 'auto' }}>
                  {clips.map((clip, i) => {
                    const isSel = selectedClip?.sfIndex === editingIdx && selectedClip?.clipIdx === i;
                    return (
                      <div key={clip.id || i}
                        onClick={() => {
                          setSelectedClip({ sfIndex: editingIdx, clipIdx: i });
                          if (editVideoRef.current) editVideoRef.current.currentTime = clip.start;
                        }}
                        style={{ padding: '10px 14px', borderBottom: '1px solid #2A344133', cursor: 'pointer', background: isSel ? 'rgba(33,150,243,0.15)' : 'transparent', borderLeft: `3px solid ${isSel ? '#2196F3' : '#2E7D32'}` }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
                          <span style={{ color: '#4CAF50', fontSize: 12, fontWeight: 700 }}>{i + 1}.</span>
                          <span style={{ color: '#6B7280', fontSize: 11 }}>{clip.start}~{clip.end}s</span>
                        </div>
                        <div style={{ fontSize: 12, color: '#C9D1D9', lineHeight: 1.4 }}>{clip.text}</div>
                      </div>
                    );
                  })}
                </div>
                {clipboard && (
                  <div style={{ padding: 12, borderTop: '1px solid #2A3441', background: '#1E2732' }}>
                    <div style={{ fontSize: 11, color: '#FF9800', marginBottom: 4 }}>📋 클립보드</div>
                    <div style={{ fontSize: 11, color: '#8B949E' }}>{clipboard.start}~{clipboard.end}s</div>
                    <div style={{ fontSize: 11, color: '#C9D1D9' }}>{clipboard.text?.slice(0, 30)}...</div>
                    <button onClick={pasteClip}
                      style={{ marginTop: 6, width: '100%', padding: '5px', background: '#2E7D32', color: '#fff', border: 'none', borderRadius: 5, fontSize: 11, cursor: 'pointer' }}>
                      여기에 붙여넣기
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>
        ) : (
          /* ════ INPUT / ANALYZING / REVIEW 패널 ════ */
          <>
            <div style={{ padding: '16px 20px', borderBottom: '1px solid #2A3441', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <span style={{ fontSize: 14, color: '#8B949E' }}>
                {shortforms.length > 0 ? `숏폼 시나리오 ${shortforms.length}개` : '분석 결과가 여기에 표시됩니다'}
              </span>
            </div>

            <div style={{ flex: 1, overflowY: 'auto', padding: 20 }}>

              {/* input 빈 상태 */}
              {phase === 'input' && shortforms.length === 0 && (
                <div style={{ textAlign: 'center', paddingTop: 80, color: '#6B7280' }}>
                  <div style={{ fontSize: 48, marginBottom: 16 }}>🎬</div>
                  <p>좌측에서 영상을 업로드하고<br/>STT + AI 분석을 시작하세요</p>
                </div>
              )}

              {/* analyzing 진행 */}
              {phase === 'analyzing' && (
                <div style={{ textAlign: 'center', paddingTop: 60, color: '#8B949E' }}>
                  <div style={{ width: 48, height: 48, border: '4px solid #2A3441', borderTop: '4px solid #4CAF50', borderRadius: '50%', animation: 'spin 1s linear infinite', margin: '0 auto 16px' }} />
                  <p style={{ fontSize: 15, marginBottom: 12 }}>{progress.message}</p>
                  <div style={{ width: 300, margin: '0 auto', background: '#2A3441', borderRadius: 8, height: 8, overflow: 'hidden' }}>
                    <div style={{ width: `${progress.percent}%`, height: '100%', background: '#4CAF50', borderRadius: 8, transition: 'width 0.3s' }} />
                  </div>
                  <span style={{ fontSize: 12, color: '#6B7280', marginTop: 8, display: 'inline-block' }}>{progress.percent}%</span>
                </div>
              )}

              {/* review 카드 */}
              {(phase === 'review' || phase === 'input') && shortforms.length > 0 && (<>
                {analysis?.analysis && (
                  <div style={{ background: 'rgba(76,175,80,0.05)', borderRadius: 12, border: '1px solid #4CAF5033', padding: 20, marginBottom: 20 }}>
                    <h4 style={{ margin: '0 0 8px', color: '#4CAF50', fontSize: 13 }}>📊 AI 영상 분석</h4>
                    <p style={{ margin: 0, fontSize: 13, color: '#C9D1D9', lineHeight: 1.6 }}>{analysis.analysis}</p>
                  </div>
                )}
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))', gap: 16 }}>
                  {shortforms.map(sf => (
                    <div key={sf.index} style={{ background: '#161B22', borderRadius: 12, border: '1px solid #2A3441', padding: 20 }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
                        <div>
                          <span style={{ color: '#4CAF50', fontWeight: 700, fontSize: 13 }}>숏폼 #{sf.index}</span>
                          <span style={{ color: '#6B7280', fontSize: 12, marginLeft: 8 }}>{sf.duration}초</span>
                        </div>
                        <div style={{ display: 'flex', gap: 6 }}>
                          <button onClick={() => handleEdit(sf)}
                            style={{ padding: '5px 14px', background: '#374151', color: '#fff', border: 'none', borderRadius: 6, fontSize: 12, cursor: 'pointer' }}>
                            ✏️ 편집
                          </button>
                          {done[sf.index] ? (
                            <a href={done[sf.index].downloadUrl}
                              style={{ padding: '6px 12px', background: '#4CAF50', color: '#fff', borderRadius: 6, fontSize: 12, textDecoration: 'none', display: 'inline-block' }}>
                              ⬇ 다운로드
                            </a>
                          ) : (
                            <button onClick={() => generate(sf)} disabled={generating[sf.index]}
                              style={{ padding: '6px 12px', background: generating[sf.index] ? '#2A3441' : '#2E7D32', color: '#fff', border: 'none', borderRadius: 6, fontSize: 12, cursor: 'pointer' }}>
                              {generating[sf.index] ? '생성 중...' : '▶ 생성'}
                            </button>
                          )}
                        </div>
                      </div>
                      <div style={{ display: 'flex', gap: 6, marginBottom: 8 }}>
                        <h4 style={{ margin: 0, fontSize: 15, color: '#E6EDF3', flex: 1 }}>{sf.title}</h4>
                        {sf.type && <span style={{ padding: '2px 8px', background: '#2E7D3233', color: '#4CAF50', borderRadius: 4, fontSize: 10, fontWeight: 600, whiteSpace: 'nowrap', alignSelf: 'flex-start' }}>{sf.type}</span>}
                      </div>
                      <p style={{ margin: '0 0 6px', fontSize: 12, color: '#8B949E', lineHeight: 1.5 }}>{sf.hook}</p>
                      {sf.why && <p style={{ margin: '0 0 4px', fontSize: 11, color: '#6B7280', lineHeight: 1.5 }}>🧠 {sf.why}</p>}
                      {sf.cta && <p style={{ margin: '0 0 12px', fontSize: 11, color: '#2196F3', lineHeight: 1.5 }}>📢 CTA: {sf.cta}</p>}
                      <div style={{ borderTop: '1px solid #2A3441', paddingTop: 10 }}>
                        {(sf.segments || []).map((seg, i) => (
                          <div key={i} style={{ display: 'flex', gap: 8, marginBottom: 4, fontSize: 11, color: '#6B7280', flexWrap: 'wrap' }}>
                            <span style={{ color: '#4CAF50', minWidth: 90, flexShrink: 0 }}>{seg.start}~{seg.end}s</span>
                            <span style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{seg.text}</span>
                            {seg.reason && <span style={{ color: '#6B7280', fontSize: 10, marginLeft: 4 }}>({seg.reason})</span>}
                          </div>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              </>)}
            </div>
          </>
        )}
      </div>

      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
    </div>
  );
}
